import { request } from '@umijs/max'
import { siesaGet } from './SiesaProxy'

const API_URL = process.env.UMI_APP_API_URL || ''
const DEBUG_ORDER_SERVICE = false

const apiRequest = async <T = any>(url: string, options: any): Promise<T> => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null
  const headers = {
    'Content-Type': 'application/json',
    ...(options?.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  }
  // Evitar duplicar slashes
  const fullUrl = API_URL ? `${API_URL.replace(/\/$/, '')}${url.startsWith('/') ? url : `/${url}`}` : url
  // Debug: log outgoing request details (URL, method, data)
  if (DEBUG_ORDER_SERVICE) {
    try {
      console.debug('ITEM3 [apiRequest] ->', { url: fullUrl, method: options?.method, data: options?.data })
    } catch {}
  }
  const timeout =
    typeof options?.timeout === 'number' && Number.isFinite(options.timeout) && options.timeout > 0
      ? Number(options.timeout)
      : 15000
  const resp: any = await request(fullUrl, { ...options, headers, timeout })
  // Umi request normalmente devuelve directamente el body; fallback a resp.data
  return (resp?.data !== undefined ? resp.data : resp) as T
}

// Interfaces para el montaje de pedidos usando el mismo backend del CRM
export interface OrderItem {
  fk_order_to_create: number
  reference: string
  description?: string
  /** Referencia "corta"/externa de SIESA (f120_descripcion_corta). */
  short_reference?: string
  extension: string
  warehouse: string
  /** Ubicación dentro de la bodega (solo bodegas multi-ubicación). */
  location?: string | null
  price_list?: string
  measure_unit: string
  quantity: number
  unit_price: number
  notes?: string
}

export interface OrderToCreate {
  order_type: string
  fk_project: number
  country_code: string
  delivery_date: string
  cost_center: string
  notes?: string
  purchase_order: string
}

export interface OrderResponse {
  id: number
  order_type: string
  fk_project: number
  fk_project_action?: number
  country_code: string
  delivery_date: string
  cost_center: string
  notes?: string
  purchase_order: string
  created_at: string
  updated_at: string
  upload?: boolean
  ready?: boolean
  order_number?: string | number
  consecutive?: string | number
}

export interface ItemResponse {
  id: number
  fk_order_to_create: number
  reference: string
  description?: string
  short_reference?: string
  item_description?: string
  item_short_description?: string
  extension: string
  warehouse: string
  location?: string | null
  price_list?: string
  measure_unit: string
  quantity: number
  unit_price: number
  notes?: string
  created_at: string
  updated_at: string
}

const ORDER_BY_ACTION_TTL_MS = 4000
const orderByActionCache = new Map<number, { expiresAt: number; value: OrderResponse | null }>()

// Tipo devuelto por el endpoint SIESA (segÃºn spec proporcionada)
export interface SiesaItem {
  reference: string
  description: string
  /** Referencia "corta"/externa del item en SIESA (f120_descripcion_corta). */
  short_description?: string
  item_id?: number
  // Some backends may expose a `name` field; keep it optional for flexibility
  name?: string
  warehouse_id: string
  warehouse_description: string
  notes?: string
  measure_unit: string
  extensions: string[]
}

// Precio por referencia / lista de precios (desde apisiesa.dsx.com.co)
export interface ItemPrice {
  reference: string
  price_list: string
  min_price: number
  max_price: number
}

/**
 * Obtener precios por referencia desde el servicio externo de Siesa (apisiesa.dsx.com.co)
 */
export const getItemPrices = async (reference: string): Promise<ItemPrice[]> => {
  try {
    if (!reference) return []
    const data = await siesaGet('get-item-price', { reference })
    return Array.isArray(data) ? (data as ItemPrice[]) : []
  } catch (err) {
    console.error('[OrderAssemblyService] Error fetching item prices:', err)
    return []
  }
}

/** Bodega del ERP (apiSiesa get-warehouses). */
export interface SiesaWarehouse {
  warehouse: string
  warehouse_name: string
  warehouse_description: string
  operative_center?: string
  /** Si es true, SIESA exige ubicación al mover existencias en esta bodega. */
  multi_location: boolean
  manage_lots?: boolean
  can_invoice?: boolean
}

/** Ubicación auxiliar dentro de una bodega (apiSiesa get-location-by-warehouse). */
export interface SiesaWarehouseLocation {
  name: string
  description: string
}

// El maestro de bodegas es corto (~40 filas) y casi nunca cambia: se pide una
// sola vez por sesión en vez de en cada ítem que se agrega al carrito.
let warehousesCache: SiesaWarehouse[] | null = null
let warehousesInFlight: Promise<SiesaWarehouse[]> | null = null
const warehouseLocationsCache = new Map<string, SiesaWarehouseLocation[]>()

const normalizeWarehouse = (raw: any): SiesaWarehouse | null => {
  const code = String(raw?.warehouse ?? '').trim()
  if (!code) return null
  const name = String(raw?.warehouse_name ?? raw?.warehouse_description ?? code).trim()
  return {
    warehouse: code,
    warehouse_name: name || code,
    warehouse_description: String(raw?.warehouse_description ?? '').trim(),
    operative_center: raw?.operative_center ? String(raw.operative_center) : undefined,
    multi_location: raw?.multi_location === true || raw?.multi_location === 1,
    manage_lots: raw?.manage_lots === true || raw?.manage_lots === 1,
    can_invoice: raw?.can_invoice === true || raw?.can_invoice === 1,
  }
}

/**
 * Maestro de bodegas activas de SIESA. Devuelve [] si el ERP no responde: el
 * montaje sigue usable con la bodega que trae el ítem.
 */
export const getWarehouses = async (forceRefresh = false): Promise<SiesaWarehouse[]> => {
  if (!forceRefresh && warehousesCache) return warehousesCache
  if (!forceRefresh && warehousesInFlight) return warehousesInFlight

  warehousesInFlight = (async () => {
    try {
      const data = await siesaGet('get-warehouses')
      const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.data) ? (data as any).data : []
      const list = rows
        .map(normalizeWarehouse)
        .filter((w: SiesaWarehouse | null): w is SiesaWarehouse => !!w)
        .sort((a: SiesaWarehouse, b: SiesaWarehouse) => a.warehouse.localeCompare(b.warehouse))
      warehousesCache = list
      return list
    } catch (err) {
      console.error('[OrderAssemblyService] Error fetching warehouses:', err)
      return warehousesCache || []
    } finally {
      warehousesInFlight = null
    }
  })()

  return warehousesInFlight
}

/**
 * Ubicaciones de una bodega. Solo tiene sentido para bodegas multi-ubicación;
 * para el resto apiSiesa devuelve vacío y el selector no se muestra.
 */
export const getWarehouseLocations = async (
  warehouse: string,
  forceRefresh = false,
): Promise<SiesaWarehouseLocation[]> => {
  const code = String(warehouse || '').trim()
  if (!code) return []
  if (!forceRefresh && warehouseLocationsCache.has(code)) {
    return warehouseLocationsCache.get(code) as SiesaWarehouseLocation[]
  }
  try {
    const data = await siesaGet('get-location-by-warehouse', { warehouse: code })
    const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.data) ? (data as any).data : []
    const list: SiesaWarehouseLocation[] = rows
      .map((r: any) => ({
        name: String(r?.name ?? '').trim(),
        description: String(r?.description ?? '').trim(),
      }))
      .filter((r: SiesaWarehouseLocation) => !!r.name)
    warehouseLocationsCache.set(code, list)
    return list
  } catch (err) {
    console.error('[OrderAssemblyService] Error fetching warehouse locations:', err)
    return []
  }
}

// Module-level in-memory cache for item extensions
const itemExtensionsCache = new Map<string, string[]>()
const itemExtensionsLoadedFull = new Set<string>()

/**
 * Obtener extensiones para una referencia. Primero intenta un endpoint específico
 * y si no está disponible hace fallback a buscar el ítem y devolver su campo extensions.
 * Soporta búsqueda incremental por filtro para references muy grandes.
 */
export const getItemExtensions = async (
  reference: string, 
  forceRefresh: boolean = false, 
  filter: string = ''
): Promise<string[]> => {
  try {
    if (!reference) return []

    const refKey = String(reference).trim().toUpperCase()
    const filterKey = String(filter || '').trim().toLowerCase()

    // Si ya cargamos todo y no hay filtro nuevo que buscar, usar caché
    if (!forceRefresh && itemExtensionsLoadedFull.has(refKey) && !filterKey) {
      const cached = itemExtensionsCache.get(refKey) || []
      console.debug('[getItemExtensions] cache hit (full) for', refKey, cached.length)
      return cached
    }

    try {
      console.debug(`[getItemExtensions] fetching extensions for ${refKey}${filter ? ` (filter: ${filter})` : ''}`)
      const collectedExts: string[] = []
      const seen = new Set<string>(itemExtensionsCache.get(refKey) || [])
      const pageSize = 500
      let page = 1
      let continueFetching = true
      const MAX_PAGES = filterKey ? 10 : 100 // Menos páginas si es búsqueda por filtro específico

      while (continueFetching && page <= MAX_PAGES) {
        let data: any
        try {
          data = await siesaGet('crm/items', {
            reference: refKey,
            limit: pageSize,
            page,
            // Si hay filtro, algunas APIs permiten enviarlo para reducir resultados
            ...(filterKey ? { extension: filterKey } : {}),
          })
        } catch (pageError) {
          console.error('[getItemExtensions] Error en página', page, pageError)
          break
        }
        if (!data) break

        const items = Array.isArray(data) ? data : (Array.isArray(data.data) ? data.data : [])
        
        if (items.length === 0) {
          continueFetching = false
          break
        }

        let foundInPage = 0
        items.forEach((it: any) => {
          if (String(it?.reference || '').trim().toUpperCase() === refKey) {
            const processExt = (e: any) => {
              const es = String(e).trim()
              if (es && !seen.has(es)) {
                // Si hay filtro, validar coincidencia si la API no lo filtró
                if (!filterKey || es.toLowerCase().includes(filterKey)) {
                  seen.add(es)
                  collectedExts.push(es)
                  foundInPage++
                }
              }
            }

            if (Array.isArray(it.extensions)) {
              it.extensions.forEach(processExt)
            } else if (it.extension) {
              processExt(it.extension)
            }
          }
        })

        // Si tenemos filtro y encontramos resultados, o si ya no hay más datos
        if (items.length < pageSize) {
          continueFetching = false
        } else if (filterKey && foundInPage > 0 && page >= 2) {
          // Si buscamos una extensión específica y ya encontramos algo, podemos parar pronto
          continueFetching = false
        } else {
          page++
        }
      }

      if (collectedExts.length > 0) {
        const existing = itemExtensionsCache.get(refKey) || []
        const combined = Array.from(new Set([...existing, ...collectedExts]))
        combined.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
        
        itemExtensionsCache.set(refKey, combined)
        if (!filterKey) itemExtensionsLoadedFull.add(refKey)
        
        console.debug(`[getItemExtensions] Total acumulado para ${refKey}: ${combined.length}`)
        return combined
      }
    } catch (e) {
      console.error('[getItemExtensions] Error fatal cargando páginas', e)
    }

    // Fallback: searchSiesaItems
    try {
      const items = await searchSiesaItems({ reference: refKey, limit: 100 })
      if (Array.isArray(items) && items.length > 0) {
        const it = items.find(x => String(x?.reference || '').trim().toUpperCase() === refKey) || items[0]
        if (it && Array.isArray((it as any).extensions)) {
          const exts = (it as any).extensions.map((e: any) => String(e))
          itemExtensionsCache.set(refKey, exts)
          itemExtensionsLoadedFull.add(refKey)
          return exts
        }
      }
    } catch (e) {}

    return itemExtensionsCache.get(refKey) || []
  } catch (err) {
    console.error('[OrderAssemblyService] Error:', err)
    return []
  }
}

// Helper to clear the in-memory cache
export const clearItemExtensionsCache = (): void => {
  itemExtensionsCache.clear()
  itemExtensionsLoadedFull.clear()
}

/**
 * Buscar items en Siesa por descripción y/o reference.
 * Va por el proxy del servidor del CRM (ver SiesaProxy.ts).
 */
export const searchSiesaItems = async (params: { term?: string; description?: string; reference?: string; name?: string; id?: number; item_id?: number; limit?: number; page?: number } = {}): Promise<SiesaItem[]> => {
  const performFetch = async (queryKey: string, value: string): Promise<any[]> => {
    try {
      // Se devuelve [] en vez de lanzar para no dejar la pantalla en blanco: el
      // buscador de items tiene que seguir respondiendo aunque falle una consulta.
      const json = await siesaGet('crm/items', {
        [queryKey]: value,
        limit: params.limit ?? 100,
        page: params.page ?? 1,
      })
      if (Array.isArray(json)) return json
      if (Array.isArray((json as any)?.data)) return (json as any).data
      return []
    } catch (err) {
      console.debug('[searchSiesaItems] fetch failed', err)
      return []
    }
  }

  // Decide query order
  const term = params.term && String(params.term).trim()
  const isReferenceLike = (t: string) => /^[A-Za-z0-9\-_.]{1,32}$/.test(t) && !/\s/.test(t)

  try {
    // If explicit fields provided, try them in preference order
    if (params.reference) {
      const data = await performFetch('reference', String(params.reference))
      return normalizeSiesaItems(data)
    }
    // support id/item_id per API docs
    if (params.item_id || params.id) {
      const idVal = String(params.item_id ?? params.id ?? '')
      const data = await performFetch('item_id', idVal)
      return normalizeSiesaItems(data)
    }
    if (params.description) {
      const data = await performFetch('description', String(params.description))
      return normalizeSiesaItems(data)
    }
    if (params.name) {
      const data = await performFetch('name', String(params.name))
      return normalizeSiesaItems(data)
    }

    // If we have a free-form term, try reference first for short tokens, else description
    if (term) {
      if (isReferenceLike(term)) {
        const byRef = await performFetch('reference', term)
        if (byRef && byRef.length) return normalizeSiesaItems(byRef)
        const byDesc = await performFetch('description', term)
        return normalizeSiesaItems(byDesc)
      } else {
        const byDesc = await performFetch('description', term)
        if (byDesc && byDesc.length) return normalizeSiesaItems(byDesc)
        const byRef = await performFetch('reference', term)
        return normalizeSiesaItems(byRef)
      }
    }

    return []
  } catch (err) {
    console.error('[OrderAssemblyService] Error searching Siesa items:', err)
    return []
  }
}

const normalizeSiesaItems = (data: any[]): SiesaItem[] => {
  if (!Array.isArray(data)) return []
  return (data as any[]).map((it) => {
    const o: any = { ...it }
    // map common id fields into item_id
    if ((o.item_id === undefined || o.item_id === null) && (o.id !== undefined)) {
      o.item_id = o.id
    }
    if (o.meassure_unit && !o.measure_unit) {
      o.measure_unit = o.meassure_unit
    }
    o.measure_unit = (o.measure_unit || '').toString().trim()
    return o as SiesaItem
  })
}

// Tipos de orden disponibles
export const ORDER_TYPES = [
  { value: "CPT", label: "Pedido de División Textil", description: "CPT" },
  { value: "CPV", label: "Pedido de Paquete Completo", description: "CPV" },
  { value: "CPE", label: "Pedido de Exportación", description: "CPE" }
]

/**
 * Bodega con la que arranca cada tipo de pedido. Sigue la misma división que
 * los centros de costo: CPT es División Textil (PT DT) y CPV/CPE son Paquete
 * Completo (PT PC).
 *
 * Es solo el valor inicial: se puede cambiar para todo el pedido en el paso de
 * información y línea por línea en el carrito.
 */
export const DEFAULT_WAREHOUSE_BY_ORDER_TYPE: Record<string, string> = {
  CPT: "PTDT",  // BODEGA PT DT
  CPV: "BDPTP", // BODEGA PT PC
  CPE: "BDPTP", // BODEGA PT PC
}

export const COUNTRY_CODES = [
  { value: '169', label: 'Colombia' },
  { value: '211', label: 'CHILE' },
  { value: '196', label: 'COSTA RICA' },
  { value: '239', label: 'ECUADOR' },
  { value: '245', label: 'ESPAÑA' },
  { value: '249', label: 'INACTIVO' },
  { value: '317', label: 'Guatemala' },
  { value: '589', label: 'PERU' },
  { value: '611', label: 'PUERTO RICO' },
  { value: '023', label: 'ALEMANIA' },
  { value: '855', label: 'VIETNAM' },
  { value: '776', label: 'TAILANDIA' },
  { value: '493', label: 'MEXICO' },
  { value: '361', label: 'INDIA' },
  { value: '365', label: 'INDONESIA' },
  { value: '215', label: 'CHINA' },
  { value: '218', label: 'ECUADOR' },
  { value: '580', label: 'PANAMA' },
  { value: '741', label: 'SINGAPUR' },
  { value: '750', label: 'SRI LANKA' },
  { value: '827', label: 'TURQUIA' },
  { value: '847', label: 'UZBEKISTAN' },
  { value: '149', label: 'CANADA' },
  { value: '850', label: 'VENEZUELA' },
  { value: '852', label: 'HONK KONG' },
  { value: '320', label: 'INACTIVO' },
  { value: '203', label: 'BRASIL' },
  { value: '372', label: 'IRLANDA' },
  { value: '188', label: 'COSTA RICA' },
  { value: '604', label: 'PERU' },
  { value: '152', label: 'CHILE' },
  { value: '840', label: 'ESTADOS UNIDOS' },
  { value: '628', label: 'REINO UNIDO' },
  { value: '521', label: 'NICARAGUA' }
]

export const COST_CENTERS: { value: string; label: string }[] = [
  { value: '060108', label: 'VENTAS Y GASTOS ALMACEN' },
  { value: '050102', label: 'VENTAS DIVISION TEXTIL' },
  { value: '060102', label: 'VENTAS PAQUETE COMPLETO' },
  { value: '060103', label: 'DISEÑO Y DESARROLLO PAQUETE COMPLETO' },
  { value: '060104', label: 'PATRONAJE VENTAS PAQUETE COMPLETO' },
  { value: '060105', label: 'TALLER DE MUESTRAS PAQUETE COMPLETO' },
  { value: '060106', label: 'CALIDAD PAQUETE COMPLETO' },
  { value: '070101', label: 'CAMPAÑA LEONISA 1' },
  { value: '070102', label: 'CAMPAÑA LEONISA 2' },
  { value: '070103', label: 'CAMPAÑA LEONISA 3' },
  { value: '070104', label: 'CAMPAÑA LEONISA 4' },
  { value: '070105', label: 'CAMPAÑA LEONISA 5' },
  { value: '070106', label: 'CAMPAÑA LEONISA 6' },
  { value: '070107', label: 'CAMPAÑA LEONISA 7' },
  { value: '070108', label: 'CAMPAÑA LEONISA 8' },
  { value: '070109', label: 'CAMPAÑA LEONISA 9' },
  { value: '070110', label: 'CAMPAÑA LEONISA 10' },
  { value: '070111', label: 'CAMPAÑA LEONISA 11' },
  { value: '070112', label: 'CAMPAÑA LEONISA 12' },
  { value: '070113', label: 'CAMPAÑA LEONISA 13' },
  { value: '070114', label: 'CAMPAÑA LEONISA 14' },
  { value: '070115', label: 'CAMPAÑA LEONISA 15' },
  { value: '070116', label: 'CAMPAÑA LEONISA 16' },
  { value: '070117', label: 'CAMPAÑA LEONISA 17' },
  { value: '070118', label: 'CAMPAÑA LEONISA 18' },
  { value: '070401', label: 'CAMPAÑA EL EXITO BETANCUR 1' },
  { value: '070402', label: 'CAMPAÑA EL EXITO BETANCUR 2' },
  { value: '070403', label: 'CAMPAÑA EL EXITO BETANCUR 3' },
  { value: '070404', label: 'CAMPAÑA EL EXITO BETANCUR 4' },
  { value: '070405', label: 'CAMPAÑA EL EXITO BETANCUR 5' },
  { value: '070609', label: 'ELA' },
  { value: '070610', label: 'STUDIO F' },
  { value: '070611', label: 'STUDIO MAN' },
  { value: '070614', label: 'OKASSIA INGRESOS' },
  { value: '060110', label: 'OKASSIA GASTOS DE VENTA' },
  { value: '050104', label: 'COLOMBIATEX DIVISION TEXTIL' },
  { value: '050105', label: 'TRANSPORTE VENTAS D.T.' },
  { value: '060111', label: 'TRANSPORTE VENTAS P.C.' },
  { value: '060112', label: 'COLOMBIATEX PAQUETE COMPLETO' }
]

/**
 * Agregar un item a una orden existente
 */
export const addItemToOrder = async (itemData: Omit<OrderItem, 'fk_order_to_create'> & { fk_order_to_create: number }): Promise<ItemResponse> => {
  try {
    // Coerce and sanitize payload to ensure backend receives expected types
    const payload: any = {
      fk_order_to_create: Number(itemData.fk_order_to_create),
      reference: itemData.reference == null ? undefined : String(itemData.reference),
      extension: itemData.extension == null ? undefined : String(itemData.extension),
      // La descripción se armaba en el panel y se perdía aquí: el payload no la
      // incluía, así que el ítem quedaba guardado sin ella aunque el backend la
      // acepta desde la migración 202603251200.
      description: itemData.description ? String(itemData.description) : undefined,
      // Referencia corta/externa: viaja aparte de la descripción porque el
      // documento del pedido las muestra en columnas distintas.
      short_reference: itemData.short_reference ? String(itemData.short_reference) : undefined,
      warehouse: itemData.warehouse == null ? undefined : String(itemData.warehouse),
      location: itemData.location ? String(itemData.location) : undefined,
      price_list: itemData.price_list == null ? undefined : String(itemData.price_list),
      measure_unit: itemData.measure_unit == null ? undefined : String(itemData.measure_unit),
      // preserve decimal precision using parseFloat and avoid accidental rounding to integers
      quantity: itemData.quantity === undefined || itemData.quantity === null ? undefined : parseFloat(String(itemData.quantity)),
      unit_price: itemData.unit_price === undefined || itemData.unit_price === null ? undefined : parseFloat(String(itemData.unit_price)),
      // allow empty string notes to be sent; only convert null/undefined to undefined
      notes: itemData.notes === undefined ? undefined : itemData.notes,
    }

    // Remove undefined keys so backend sees a compact payload
    Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k])

    if (DEBUG_ORDER_SERVICE) console.debug('ITEM3 [OrderAssemblyService] addItemToOrder payload:', payload)
    try {
      // Also log JSON serialization so we can verify exact body sent
      if (DEBUG_ORDER_SERVICE) console.debug('ITEM3 [OrderAssemblyService] addItemToOrder payload JSON:', JSON.stringify(payload))
    } catch (e) {}
    const response = await apiRequest<ItemResponse>('/item-of-order', {
      method: 'POST',
      data: payload,
    })
    console.debug('[OrderAssemblyService] addItemToOrder response:', response)
    return response
  } catch (error) {
    console.error('Error agregando item:', error)
    throw error
  }
}

/**
 * Crear un nuevo order-to-create (POST /order-to-create)
 */
export const createOrderToCreate = async (payload: Partial<OrderToCreate>): Promise<OrderResponse> => {
  try {
    const response = await apiRequest<OrderResponse>('/order-to-create', {
      method: 'POST',
      data: payload,
    })
    return response
  } catch (error) {
    console.error('Error creando order-to-create:', error)
    throw error
  }
}

/**
 * Obtener órdenes por proyecto
 */
export const getOrdersByProject = async (projectId: number): Promise<OrderResponse[]> => {
  try {
    const response = await apiRequest<OrderResponse[]>(`/order-to-create?fk_project=${projectId}`, {
      method: 'GET',
      timeout: 12000,
    })
    return response
  } catch (error: any) {
    const status = Number(error?.response?.status || error?.status || 0)
    if (status >= 500 || status === 404) return []
    console.error('Error obteniendo órdenes:', error)
    throw error
  }
}

/**
 * Obtener items de una orden
 */
export const getOrderItems = async (orderToCreateId: number): Promise<ItemResponse[]> => {
  try {
    const response: any = await apiRequest(`/item-of-order?fk_order_to_create=${orderToCreateId}`, {
      method: 'GET',
      timeout: 12000,
    })
    // The backend may return either a flat array of ItemResponse or an array of orders grouped with .items
    // Normalize to a flat ItemResponse[] for the client.
    if (!response) return []
    // If response is an object with data property (apiRequest may have returned a wrapper), try to unwrap
    const payload = Array.isArray(response) ? response : (response.data ?? response)
    // If payload is an array, it may be either:
    //  - an array of orders where each order has `.items`
    //  - a flat array of item objects (possibly containing items from multiple orders)
    if (Array.isArray(payload)) {
      // If the array contains order objects with .items, try to find the specific order first
      if (payload.length && payload[0] && Array.isArray(payload[0].items)) {
        // Try to locate the order matching the requested id
        const match = payload.find((order: any) => Number(order.id) === Number(orderToCreateId) || Number(order.fk_order_to_create) === Number(orderToCreateId))
        if (match && Array.isArray(match.items)) {
          return match.items as ItemResponse[]
        }

        // Fallback: flatten all items but filter by fk_order_to_create to avoid returning unrelated items
        const flat: ItemResponse[] = []
        payload.forEach((order: any) => {
          if (Array.isArray(order.items)) {
            order.items.forEach((it: any) => flat.push(it as ItemResponse))
          }
        })
        return flat.filter((it) => Number(it.fk_order_to_create) === Number(orderToCreateId))
      }

      // Otherwise payload is likely already a flat ItemResponse[]; filter by fk_order_to_create
      return (payload as any[]).filter((it) => Number(it.fk_order_to_create) === Number(orderToCreateId)) as ItemResponse[]
    }

    return []
  } catch (error) {
    // Si el backend responde 500, devolvemos lista vacÃ­a para no saturar la UI con errores
    const status = (error as any)?.response?.status
    if (status === 500) {
      return []
    }
    console.error('Error obteniendo items:', error)
    throw error
  }
}

/**
 * Actualizar un item de orden
 */
export const updateOrderItem = async (itemId: number, itemData: Partial<OrderItem>): Promise<ItemResponse> => {
  try {
    // sanitize numeric fields similarly to addItemToOrder
    const data = {
      ...itemData,
      quantity: itemData.quantity === undefined || itemData.quantity === null ? undefined : parseFloat(String(itemData.quantity)),
      unit_price: itemData.unit_price === undefined || itemData.unit_price === null ? undefined : parseFloat(String(itemData.unit_price)),
      notes: itemData.notes === undefined ? undefined : itemData.notes,
    }
    const response = await apiRequest<ItemResponse>(`/item-of-order/${itemId}`, {
      method: 'PATCH',
      data,
    })
    return response
  } catch (error) {
    console.error('Error actualizando item:', error)
    throw error
  }
}

/**
 * Eliminar un item de orden
 */
export const deleteOrderItem = async (itemId: number): Promise<void> => {
  try {
    await apiRequest(`/item-of-order/${itemId}`, {
      method: 'DELETE',
    })
  } catch (error) {
    console.error('Error eliminando item:', error)
    throw error
  }
}

/**
 * Listar pedidos con filtros opcionales (pasar upload=false para pendientes)
 */
export const listOrders = async (params: Record<string, any> = {}): Promise<OrderResponse[]> => {
  try {
    const search = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') search.append(k, String(v))
    })
    const query = search.toString()
    const response = await apiRequest<OrderResponse[]>(`/order-to-create${query ? `?${query}` : ''}`, {
      method: 'GET',
      timeout: 12000,
    })
    return response
  } catch (error: any) {
    const status = Number(error?.response?.status || error?.status || 0)
    if (status >= 500 || status === 404) return []
    console.error('Error listando pedidos:', error)
    throw error
  }
}

/**
 * Actualizar un order-to-create (por ejemplo para marcar ready=true)
 */
export const updateOrderToCreate = async (orderId: number, data: Partial<OrderToCreate & { ready?: boolean; upload?: boolean; fk_project_action?: number }>): Promise<OrderResponse> => {
  try {
    const response = await apiRequest<OrderResponse>(`/order-to-create/${orderId}`, {
      method: 'PATCH',
      data,
    })
    return response
  } catch (error) {
    console.error('Error actualizando order:', error)
    throw error
  }
}

/**
 * Ejecutar upload manualmente (POST /upload-order)
 */
export const uploadOrders = async (): Promise<any> => {
  try {
    const response = await apiRequest('/upload-order', { method: 'POST' })
    return response
  } catch (error) {
    console.error('Error ejecutando upload-orders:', error)
    throw error
  }
}

/**
 * Subir un pedido especÃ­fico a SIESA (POST /upload-order/:id)
 */
export const uploadSingleOrder = async (orderId: number): Promise<any> => {
  try {
    const response = await apiRequest(`/upload-order/${orderId}`, { method: 'POST' })
    return response
  } catch (error) {
    console.error('Error subiendo pedido a SIESA:', error)
    throw error
  }
}

/**
 * Obtener el OrderToCreate vinculado a un ProjectAction (si existe).
 * Devuelve null si no hay pedido asociado.
 */
export const getOrderByActionId = async (actionId: number): Promise<OrderResponse | null> => {
  const normalizedActionId = Number(actionId)
  if (!Number.isFinite(normalizedActionId) || normalizedActionId <= 0) return null

  const cached = orderByActionCache.get(normalizedActionId)
  const now = Date.now()
  if (cached && cached.expiresAt > now) return cached.value

  try {
    const response = await apiRequest<OrderResponse[]>(`/order-to-create?fk_project_action=${normalizedActionId}`, {
      method: 'GET',
      timeout: 10000,
    })
    const orders = Array.isArray(response) ? response : []
    const resolved = orders.length > 0 ? orders[0] : null
    orderByActionCache.set(normalizedActionId, {
      value: resolved,
      expiresAt: now + ORDER_BY_ACTION_TTL_MS,
    })
    return resolved
  } catch (error: any) {
    const topStatus = Number(error?.response?.status || error?.status || 0)
    const nestedStatus = Number(error?.response?.data?.status || 0)
    const detail = String(error?.response?.data?.message || error?.message || '').toLowerCase()
    const isNotFound =
      topStatus === 404
      || nestedStatus === 404
      || detail.includes('not found')
      || detail.includes('order to create not found')
    const isRecoverableServerError = topStatus >= 500 || nestedStatus >= 500

    if (!isNotFound && !isRecoverableServerError) {
      console.error('Error obteniendo pedido por acción:', error)
    } else if (DEBUG_ORDER_SERVICE) {
      console.debug('[OrderAssemblyService] getOrderByActionId fallback a null', {
        actionId: normalizedActionId,
        topStatus,
        nestedStatus,
        detail,
      })
    }
    orderByActionCache.set(normalizedActionId, {
      value: null,
      expiresAt: now + ORDER_BY_ACTION_TTL_MS,
    })
    return null
  }
}

export const clearOrderAssemblyServiceCache = (): void => {
  orderByActionCache.clear()
  warehousesCache = null
  warehouseLocationsCache.clear()
}




/** Orden de compra del cliente leída y traducida a SIESA (POST /order-to-create/read-purchase-order). */
export interface PurchaseOrderRead {
  header: { order: string | null; order_date: string | null; bill_to_name: string | null; bill_to_nit: string | null; campaign: string | null }
  totals: { quantity: number; value: number } | null
  problems: string[]
  warnings: string[]
  defaults: { warehouse: string; price_list: string; purchase_order: string | null; delivery_date: string | null }
  client_match: { id: number; name: string; nit: string; branch: string } | null
  lines: Array<{
    line: number
    client_reference: string
    description: string
    comment: string | null
    quantity: number
    unit_price: number
    reference: string | null
    reference_source: string | null
    extension: string | null
    siesa_description: string | null
    siesa_measure_unit: string | null
    problems: string[]
    warnings: string[]
  }>
}

export const readPurchaseOrder = async (fkProjectActionId: number, file: File): Promise<PurchaseOrderRead> => {
  // Skipper necesita los campos de texto antes del archivo.
  const body = new FormData()
  body.append('fk_project_action', String(fkProjectActionId))
  body.append('file', file)
  const token = localStorage.getItem('token')
  const res = await fetch(`${API_URL.replace(/\/$/, '')}/order-to-create/read-purchase-order`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body,
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data?.message || 'No se pudo leer la orden de compra')
  return data as PurchaseOrderRead
}

/** Pasa la acción de pedido al cliente del grupo cuyo NIT es el de la OC. */
export const moveOrderActionToClient = async (fkProjectActionId: number, fkClient: number) =>
  apiRequest<{ fk_project: number; project_name: string; client: { name: string } }>('/order-to-create/move-action-to-client', {
    method: 'POST',
    data: { fk_project_action: fkProjectActionId, fk_client: fkClient },
  })
